import { MultipleSelect } from '@kalos/ui';
import { type FC, useMemo } from 'react';

import {
  useGroupMemberListQuery,
  useOrganizationGroups,
} from '../../../hooks/react-query/useOrgStructureQuery';
import { useBatchUserQuery } from '../../../hooks/react-query/useUserQuery';

/**
 * The teams an employee is on (ClickUp 86bbfvuyb).
 *
 * Multi-select because people genuinely belong to several: someone splitting
 * time between two crews is on both, and the seed sheet already carries people
 * with two teams.
 *
 * Built on the shared MultipleSelect, which scrolls inside this dialog where a
 * hand-rolled Command list did not. Its options are flat {value,label} strings,
 * so everything that identifies a group — where it sits, who is in it — has to
 * fit on one line rather than stack under the name.
 *
 * Controlled, and deliberately so. Teams are `group_member` rows rather than a
 * column of `userz`, so an earlier version wrote each toggle straight through —
 * which meant the form's Save button stayed dead while the change had already
 * happened, and Cancel did not undo it. The selection is state now; the host
 * form owns it, Save applies it through `useApplyTeamChanges`.
 */
export const TeamMultiSelect: FC<{
  /** Selected team ids. */
  value: number[];
  onChange: (next: number[]) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled = false }) => {
  // Every team, not just the top-level ones. A box nested inside a team page
  // is still a team someone is put on — it is how the chart draws a person
  // under a supervisor — so restricting this to roots made every box on a page
  // unassignable the moment it was nested.
  const { groups: teams, pathById, pages, childrenByParent } = useOrganizationGroups();
  const membersQuery = useGroupMemberListQuery();
  // Same filters as the chart's own employee fetch, so the two share a cache
  // entry rather than each paying for the list.
  const usersQuery = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      isActive: 1,
      overrideLimit: true,
      orderBy: 'user_lastname',
      orderDir: 'ASC',
    },
  });

  /**
   * Who is already in each group, and which of several same-named siblings it
   * is.
   *
   * Sibling groups legitimately share a name — the Electrical page draws three
   * called "ES Lead Superintendent", one per crew — so neither the name nor the
   * ancestry tells them apart. Who is in it does, and it is what someone
   * assigning a person actually recognises.
   */
  const hintById = useMemo(() => {
    const nameById = new Map(
      (usersQuery.data?.results ?? []).map((u) => [
        u.id,
        {
          full: `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim(),
          sort: `${u.lastname ?? ''} ${u.firstname ?? ''}`.trim().toLowerCase(),
        },
      ]),
    );

    const namesByGroup = new Map<number, { full: string; sort: string }[]>();
    for (const m of membersQuery.data?.results ?? []) {
      if (!m.isActive) continue;
      const who = nameById.get(m.userId);
      if (!who?.full) continue;
      const list = namesByGroup.get(m.organizationGroupId) ?? [];
      list.push(who);
      namesByGroup.set(m.organizationGroupId, list);
    }

    // Position among siblings that share this name, in the order the chart
    // draws them left to right.
    const positions = new Map<number, string>();
    const number = (siblings: { id: number; name: string }[]) => {
      for (const group of siblings) {
        const same = siblings.filter(
          (s) => s.name.trim().toLowerCase() === group.name.trim().toLowerCase(),
        );
        if (same.length > 1) {
          positions.set(
            group.id,
            `${same.findIndex((s) => s.id === group.id) + 1} of ${same.length}`,
          );
        }
      }
    };
    number(pages);
    for (const [, kids] of childrenByParent) number(kids);

    const out = new Map<number, string>();
    for (const team of teams) {
      const parts: string[] = [];
      const position = positions.get(team.id);
      if (position) parts.push(position);

      // By surname, as every other list of people in the app is ordered.
      const who = [...(namesByGroup.get(team.id) ?? [])]
        .sort((a, b) => a.sort.localeCompare(b.sort))
        .map((n) => n.full);
      if (who.length > 0) {
        // Two names and a count: the label doubles as the badge for a picked
        // group, and a full roster there would wrap the field.
        parts.push(
          who.length > 2 ? `${who.slice(0, 2).join(', ')} +${who.length - 2}` : who.join(', '),
        );
      } else if (position) {
        parts.push('empty');
      }

      if (parts.length > 0) out.set(team.id, parts.join(' · '));
    }
    return out;
  }, [teams, pages, childrenByParent, membersQuery.data, usersQuery.data]);

  /**
   * Ordered by full path, so a page's boxes sit under it rather than scattered
   * through one long alphabetical list.
   */
  const sorted = useMemo(
    () =>
      [...teams].sort((a, b) =>
        (pathById.get(a.id) ?? a.name).localeCompare(pathById.get(b.id) ?? b.name, undefined, {
          sensitivity: 'base',
        }),
      ),
    [teams, pathById],
  );

  /**
   * One line per group: its name, where it sits, then who is in it. Group names
   * repeat across pages — every page has a "President" — so the bare name
   * identifies nothing on its own.
   *
   * The label is also the badge shown once a group is picked, so the hint is
   * kept short — two names and a count rather than the whole roster.
   */
  const options = useMemo(
    () =>
      sorted.map((t) => {
        const segments = (pathById.get(t.id) ?? '').split(' › ');
        const where = segments.slice(0, -1).join(' › ');
        const hint = hintById.get(t.id);
        return {
          value: String(t.id),
          label: `${t.name}${where ? ` — ${where}` : ''}${hint ? ` · ${hint}` : ''}`,
        };
      }),
    [sorted, hintById, pathById],
  );

  return (
    <MultipleSelect
      options={options}
      selected={value.map(String)}
      onChange={(next) => onChange(next.map(Number).filter((id) => id > 0))}
      placeholder="Unassigned"
      disabled={disabled}
      listClassName="max-h-64"
    />
  );
};
